<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);

const accessHours = ref({
  monday: { start: '', end: '' },
  tuesday: { start: '', end: '' },
  wednesday: { start: '', end: '' },
  thursday: { start: '', end: '' },
  friday: { start: '', end: '' },
  saturday: { start: '', end: '' },
  sunday: { start: '', end: '' },
});
const dayLabels = computed(() => ({
  monday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.MONDAY'),
  tuesday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.TUESDAY'),
  wednesday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.WEDNESDAY'),
  thursday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.THURSDAY'),
  friday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.FRIDAY'),
  saturday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.SATURDAY'),
  sunday: t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DAYS.SUNDAY'),
}));

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  accessHours.value = {
    monday: { start: '', end: '' },
    tuesday: { start: '', end: '' },
    wednesday: { start: '', end: '' },
    thursday: { start: '', end: '' },
    friday: { start: '', end: '' },
    saturday: { start: '', end: '' },
    sunday: { start: '', end: '' },
  };
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];

  if (
    props.selectedRole.access_hours &&
    Object.keys(props.selectedRole.access_hours).length > 0
  ) {
    accessHours.value = {
      ...accessHours.value,
      ...props.selectedRole.access_hours,
    };
  }
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const modalTitle = computed(() =>
  props.mode === 'edit'
    ? t('CUSTOM_ROLE.EDIT.TITLE')
    : t('CUSTOM_ROLE.ADD.TITLE')
);
const modalDescription = computed(() =>
  props.mode === 'edit' ? t('CUSTOM_ROLE.EDIT.DESC') : t('CUSTOM_ROLE.ADD.DESC')
);
const submitButtonText = computed(() =>
  props.mode === 'edit'
    ? t('CUSTOM_ROLE.EDIT.SUBMIT')
    : t('CUSTOM_ROLE.ADD.SUBMIT')
);

const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](currentAccountId.value)
);
const currentAccountPermissions = computed(
  () => currentAccount.value?.permissions || []
);
const fullAccessCorePermissions = [
  'account_manage',
  'agent_manage',
  'team_manage',
  'inbox_manage',
  'custom_role_manage',
  'security_manage',
];
const canGrantLumeraAppBillingView = computed(
  () =>
    currentAccount.value?.role === 'administrator' ||
    currentAccountPermissions.value.some(permission =>
      ['account_manage', 'billing_manage'].includes(permission)
    ) ||
    fullAccessCorePermissions.every(permission =>
      currentAccountPermissions.value.includes(permission)
    )
);

const permissionModules = {
  lumerai_manage: 'ia',
  workflow_manage: 'mini_ai_workflow',
  conversation_workflow_manage: 'mini_ai_workflow',
  automation_manage: 'automations',
  macro_manage: 'macros',
  canned_response_manage: 'canned_responses',
  campaign_manage: 'campaigns',
  ticket_manage: 'tickets',
  report_manage: 'reports',
  sla_manage: 'sla',
  alert_manage: 'alerts',
  api_manage: 'api',
  sms_manage: 'sms',
  custom_tool_manage: 'custom_tools',
  white_label_manage: 'white_label',
  integration_manage: 'integrations',
  crm_manage: 'crm',
  company_manage: 'crm',
  calendar_manage: 'calendar',
  agent_manage: 'agent_management',
  team_manage: 'team_management',
  inbox_manage: 'inbox_management',
  label_manage: 'labels',
  custom_attribute_manage: 'custom_attributes',
  audit_log_manage: 'audit_logs',
  knowledge_base_manage: 'help_center',
  custom_role_manage: 'custom_roles',
  staff_sticker_manage: 'staff_stickers',
  webhook_manage: 'integrations',
};

const hasModule = moduleName => {
  const account = store.getters['accounts/getAccount'](currentAccountId.value);
  if (!account) return false;

  const readFlag = source =>
    Array.isArray(source)
      ? source.includes(moduleName)
      : source[moduleName] === true || source[moduleName] === 'true';

  return (
    readFlag(account.enabled_modules || {}) || readFlag(account.features || {})
  );
};

const isPermissionLocked = permission => {
  if (
    permission === 'lumera_app_billing_view' &&
    !canGrantLumeraAppBillingView.value
  ) {
    return true;
  }

  const moduleName = permissionModules[permission];
  return moduleName ? !hasModule(moduleName) : false;
};

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
      access_hours: accessHours.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const accessHoursValid = computed(() =>
  Object.values(accessHours.value).every(
    hours => (!hours.start && !hours.end) || (hours.start && hours.end)
  )
);

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid || !accessHoursValid.value || addCustomRole.showLoading
);

const permissionCategories = computed(() => {
  const perms = AVAILABLE_CUSTOM_ROLE_PERMISSIONS;
  return {
    CONVERSATIONS: perms.filter(p =>
      [
        'conversation_manage',
        'conversation_unassigned_manage',
        'conversation_participating_manage',
        'ticket_manage',
      ].includes(p)
    ),
    CONTACTS_CRM: perms.filter(p =>
      ['contact_manage', 'company_manage', 'crm_manage'].includes(p)
    ),
    CALENDAR: perms.filter(p => ['calendar_manage'].includes(p)),
    AUTOMATION_AI: perms.filter(p =>
      [
        'lumerai_manage',
        'workflow_manage',
        'automation_manage',
        'macro_manage',
        'canned_response_manage',
        'conversation_workflow_manage',
        'alert_manage',
        'custom_tool_manage',
      ].includes(p)
    ),
    LUMERA_APP: perms.filter(p => ['lumera_app_billing_view'].includes(p)),
    MANAGEMENT_SETTINGS: perms.filter(p =>
      [
        'account_manage',
        'agent_manage',
        'team_manage',
        'inbox_manage',
        'custom_role_manage',
        'security_manage',
        'audit_log_manage',
        'integration_manage',
        'location_manage',
        'staff_sticker_manage',
        'sms_manage',
        'billing_manage',
      ].includes(p)
    ),
    OTHER: perms.filter(p =>
      [
        'report_manage',
        'knowledge_base_manage',
        'campaign_manage',
        'label_manage',
        'custom_attribute_manage',
        'sla_manage',
        'api_manage',
        'webhook_manage',
        'white_label_manage',
      ].includes(p)
    ),
  };
});

const toggleAllCategory = categoryPermissions => {
  const allSelected = categoryPermissions.every(
    p => isPermissionLocked(p) || selectedPermissions.value.includes(p)
  );
  if (allSelected) {
    selectedPermissions.value = selectedPermissions.value.filter(
      p => !categoryPermissions.includes(p)
    );
  } else {
    selectedPermissions.value = [
      ...new Set([
        ...selectedPermissions.value,
        ...categoryPermissions.filter(p => !isPermissionLocked(p)),
      ]),
    ];
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, @intlify/vue-i18n/no-dynamic-keys -->
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 mt-2">
          <div
            v-for="(perms, category) in permissionCategories"
            :key="category"
            class="bg-n-alpha-1 rounded-xl p-4 border border-n-strong flex flex-col"
          >
            <div
              class="flex justify-between items-center mb-3 border-b border-n-strong pb-2"
            >
              <h4 class="font-semibold text-n-slate-12 text-sm">
                {{ $t(`CUSTOM_ROLE.FORM.PERMISSION_CATEGORIES.${category}`) }}
              </h4>
              <button
                v-if="perms.length > 0"
                type="button"
                class="text-xs text-n-brand hover:underline font-medium"
                @click.prevent="toggleAllCategory(perms)"
              >
                {{ $t('CUSTOM_ROLE.FORM.SELECT_ALL') }}
              </button>
            </div>
            <div class="flex flex-col gap-2.5">
              <div
                v-for="permission in perms"
                :key="permission"
                class="flex items-center"
              >
                <input
                  :id="permission"
                  v-model="selectedPermissions"
                  type="checkbox"
                  :value="permission"
                  name="permissions"
                  :disabled="isPermissionLocked(permission)"
                  class="ltr:mr-2 rtl:ml-2 rounded border-slate-300 text-n-brand focus:ring-n-brand"
                />
                <label
                  :for="permission"
                  class="text-sm font-normal text-n-slate-11 cursor-pointer select-none"
                  :class="{
                    'opacity-50 cursor-not-allowed':
                      isPermissionLocked(permission),
                  }"
                >
                  <span
                    v-if="isPermissionLocked(permission)"
                    class="i-lucide-lock-keyhole inline-block mr-1 text-xs"
                    :title="$t('CUSTOM_ROLE.FORM.AVAILABLE_IN_STORE')"
                  />
                  {{
                    $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
                  }}
                  <span
                    v-if="isPermissionLocked(permission)"
                    class="text-xs text-n-slate-10 ml-1"
                  >
                    ({{ $t('CUSTOM_ROLE.FORM.AVAILABLE_IN_STORE_SHORT') }})
                  </span>
                </label>
              </div>
              <div
                v-if="perms.length === 0"
                class="text-xs text-n-slate-10 italic"
              >
                {{ $t('CUSTOM_ROLE.FORM.NO_PERMISSIONS') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full mt-4">
        <label class="font-medium text-sm">{{
          $t('CUSTOM_ROLE.FORM.ACCESS_HOURS.LABEL')
        }}</label>
        <span class="text-xs text-n-slate-11 block mb-2">
          {{ $t('CUSTOM_ROLE.FORM.ACCESS_HOURS.DESCRIPTION') }}
        </span>

        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div
            v-for="day in [
              'monday',
              'tuesday',
              'wednesday',
              'thursday',
              'friday',
              'saturday',
              'sunday',
            ]"
            :key="day"
            class="flex min-h-20 min-w-0 items-center gap-3 rounded-lg bg-n-solid-2 px-3 py-3"
          >
            <label
              :for="`access-start-${day}`"
              class="flex h-10 min-w-0 flex-1 items-center whitespace-nowrap text-sm font-medium"
            >
              {{ dayLabels[day] }}
            </label>
            <div class="grid min-w-0 translate-y-2 grid-cols-2 gap-2">
              <input
                :id="`access-start-${day}`"
                v-model="accessHours[day].start"
                type="time"
                :aria-label="
                  $t('CUSTOM_ROLE.FORM.ACCESS_HOURS.START_ARIA', {
                    day: dayLabels[day],
                  })
                "
                class="h-10 w-28 min-w-0 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm"
              />
              <input
                :id="`access-end-${day}`"
                v-model="accessHours[day].end"
                type="time"
                :aria-label="
                  $t('CUSTOM_ROLE.FORM.ACCESS_HOURS.END_ARIA', {
                    day: dayLabels[day],
                  })
                "
                class="h-10 w-28 min-w-0 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm"
              />
            </div>
          </div>
        </div>
        <p v-if="!accessHoursValid" role="alert" class="text-sm text-n-ruby-11">
          {{ $t('CUSTOM_ROLE.FORM.ACCESS_HOURS.ERROR') }}
        </p>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
